// Compressible Flow in Fluid Dynamics

const S = {
  wrapper: { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Diagram 1: Mach Number Regimes ───────────────────────────────
function DiagramMachRegimes() {
  const W = 900, H = 280;

  // Regimes: subsonic, transonic, sonic, supersonic, hypersonic
  const regimes = [
    { x:30,  w:140, label:'Subsonic',    sub:'Ma < 0.8',    fill:'#e8f4e8', stroke:'#27ae60',  tc:'#145214' },
    { x:175, w:120, label:'Transonic',   sub:'0.8–1.2',     fill:'#fff5e6', stroke:'#e07b00',  tc:'#8B4513' },
    { x:300, w:80,  label:'Sonic',       sub:'Ma = 1.0',    fill:'#FEF0F0', stroke:'#c0392b',  tc:'#7B0D0D' },
    { x:385, w:200, label:'Supersonic',  sub:'1.2 < Ma < 5',fill:'#f5e6ff', stroke:'#9b59b6',  tc:'#5b2c8d' },
    { x:590, w:200, label:'Hypersonic',  sub:'Ma > 5',      fill:'#fff0e6', stroke:'#c0392b',  tc:'#7B0D0D' },
  ];

  // Speed of sound reference line
  const machScale = (ma) => 30 + (ma / 8) * 780;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Mach Number Regimes — Flow Classification by Speed</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="axArr" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
            <polygon points="0 0, 7 4, 0 8" fill="#333"/>
          </marker>
          <marker id="mArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
        </defs>

        {/* Regime bands */}
        {regimes.map(({x,w,label,sub,fill,stroke,tc}) => (
          <g key={label}>
            <rect x={x} y={40} width={w} height={100} rx={4} fill={fill} stroke={stroke} strokeWidth="1.5"/>
            <text x={x+w/2} y={82} textAnchor="middle" fontSize="13" fill={tc} fontWeight="700">{label}</text>
            <text x={x+w/2} y={100} textAnchor="middle" fontSize="11" fill={tc}>{sub}</text>
          </g>
        ))}

        {/* Ma axis */}
        <line x1={28} y1={170} x2={820} y2={170} stroke="#333" strokeWidth="2" markerEnd="url(#axArr)"/>
        <text x={830} y={174} fontSize="13" fill="#333" fontWeight="700">Ma</text>

        {/* Tick marks and labels */}
        {[0,0.5,1,1.5,2,3,4,5,6,7,8].map(ma => {
          const x = machScale(ma);
          return (
            <g key={ma}>
              <line x1={x} y1={166} x2={x} y2={174} stroke="#333" strokeWidth="1.5"/>
              <text x={x} y={186} textAnchor="middle" fontSize="11" fill="#555">{ma}</text>
            </g>
          );
        })}

        {/* Notable aircraft/phenomena markers */}
        {[
          { ma:0.85, label:'Commercial\nairliner', color:'#27ae60' },
          { ma:1.0,  label:'Sound\nbarrier',       color:'#c0392b' },
          { ma:2.0,  label:'Concorde',              color:'#9b59b6' },
          { ma:3.0,  label:'SR-71',                 color:'#9b59b6' },
          { ma:5.5,  label:'X-43A\nhypersonic',     color:'#e07b00' },
          { ma:7.5,  label:'Space\nShuttle\nre-entry', color:'#c0392b' },
        ].map(({ma, label, color}) => {
          const x = machScale(ma);
          const lines = label.split('\n');
          return (
            <g key={ma}>
              <line x1={x} y1={148} x2={x} y2={170} stroke={color} strokeWidth="1.5" strokeDasharray="3,2"/>
              <circle cx={x} cy={148} r={4} fill={color}/>
              {lines.map((l,i) => (
                <text key={i} x={x} y={132 - (lines.length-1-i)*14} textAnchor="middle" fontSize="10" fill={color}>{l}</text>
              ))}
            </g>
          );
        })}

        {/* Compressibility note */}
        <rect x={30} y={200} width={380} height={60} rx={4} fill="#fff5f5" stroke="#c0392b" strokeWidth="1"/>
        <text x={38} y={218} fontSize="12" fill="#7B0D0D" fontWeight="700">Compressibility matters when Ma &gt; 0.3</text>
        <text x={38} y={234} fontSize="11" fill="#444">Below Ma 0.3: density changes &lt; 5% — treat as incompressible</text>
        <text x={38} y={250} fontSize="11" fill="#444">Above Ma 0.3: density, temperature, pressure all vary significantly</text>

        <rect x={430} y={200} width={370} height={60} rx={4} fill="#f5e6ff" stroke="#9b59b6" strokeWidth="1"/>
        <text x={438} y={218} fontSize="12" fill="#5b2c8d" fontWeight="700">Speed of sound: a = √(γRT)</text>
        <text x={438} y={234} fontSize="11" fill="#444">γ = 1.4 (air), R = 287 J/kg·K</text>
        <text x={438} y={250} fontSize="11" fill="#444">At sea level (T=288 K): a ≈ 340 m/s ≈ 1225 km/h</text>
      </svg>
      <div style={S.dCaption}>The Mach number Ma = U/a (flow speed / local speed of sound) classifies compressible flow into regimes with qualitatively different physics. In subsonic flow density changes are negligible; in transonic flow shock waves first appear on the body; in supersonic flow a bow shock stands ahead of the body; in hypersonic flow aerodynamic heating becomes critical. The speed of sound itself depends on temperature: a = √(γRT).</div>
    </div>
  );
}

// ── Diagram 2: Sound Wave Propagation & Mach Cone ─────────────────
function DiagramMachCone() {
  const W = 900, H = 340;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Sound Wave Propagation — Subsonic, Sonic & Supersonic Sources</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="spArr" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#c0392b"/>
          </marker>
        </defs>

        {/* ── Panel A: Subsonic ── */}
        {(()=>{
          const ox = 10, oy = 10, pw = (PW - 10);
          const sx = ox + pw*0.55, sy = oy + (H-20)/2;
          return <>
            <rect x={ox} y={oy} width={pw} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
            <text x={ox+pw/2} y={oy+22} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Subsonic Source (Ma &lt; 1)</text>

            {/* Concentric wavefronts — source ahead of centre */}
            {[30,60,90,120].map((r,i) => (
              <circle key={r} cx={sx - i*12} cy={sy} r={r}
                fill="none" stroke="#c0392b" strokeWidth="1.4" opacity={0.55 - i*0.05}
                strokeDasharray={i===0?"none":"4,3"}/>
            ))}
            {/* Source dot */}
            <circle cx={sx} cy={sy} r={6} fill="#c0392b"/>
            <text x={sx+10} y={sy-10} fontSize="11" fill="#c0392b" fontWeight="700">Source</text>

            {/* Velocity arrow */}
            <line x1={sx-70} y1={sy+25} x2={sx-20} y2={sy+25} stroke="#27ae60" strokeWidth="2" markerEnd="url(#spArr)"/>
            <text x={sx-75} y={sy+42} fontSize="11" fill="#27ae60" fontWeight="700">U &lt; a</text>

            {/* Observer can hear approach */}
            <text x={ox+8} y={sy-110} fontSize="11" fill="#555">Waves crowd ahead</text>
            <text x={ox+8} y={sy-96} fontSize="11" fill="#555">(higher freq. ahead)</text>
            <text x={ox+8} y={sy-82} fontSize="11" fill="#555">→ Doppler effect</text>

            <rect x={ox+6} y={H-68} width={pw-12} height={44} rx={3} fill="#e8f4e8" stroke="#27ae60" strokeWidth="1"/>
            <text x={ox+12} y={H-52} fontSize="11" fill="#145214" fontWeight="700">Pressure disturbances propagate ahead of source</text>
            <text x={ox+12} y={H-38} fontSize="11" fill="#444">Fluid is pre-warned → smooth flow around body</text>
            <text x={ox+12} y={H-24} fontSize="11" fill="#444">No shocks possible</text>
          </>;
        })()}

        {/* ── Panel B: Supersonic — Mach cone ── */}
        {(()=>{
          const ox = PW + 20, oy = 10, pw = PW - 10;
          const sx = ox + pw*0.35, sy = oy + (H-20)/2;
          const ma = 2.0;
          // Mach angle: μ = arcsin(1/Ma)
          const mu = Math.asin(1/ma); // radians
          const coneH = 180;
          const coneW = coneH / Math.tan(mu);

          return <>
            <rect x={ox} y={oy} width={pw} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
            <text x={ox+pw/2} y={oy+22} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Supersonic Source (Ma = 2.0)</text>

            {/* Past wavefronts left behind */}
            {[40,80,120,155].map((r,i) => (
              <circle key={r} cx={sx - i*50} cy={sy} r={r}
                fill="none" stroke="#c0392b" strokeWidth="1.3" opacity={0.4}
                strokeDasharray="4,3"/>
            ))}

            {/* Mach cone lines */}
            <line x1={sx} y1={sy} x2={sx + coneW} y2={sy - coneH}
              stroke="#c0392b" strokeWidth="2.5"/>
            <line x1={sx} y1={sy} x2={sx + coneW} y2={sy + coneH}
              stroke="#c0392b" strokeWidth="2.5"/>

            {/* Zone of silence shading */}
            <path d={`M ${sx},${sy} L ${sx+coneW},${sy-coneH} L ${ox+pw-8},${sy-coneH} L ${ox+pw-8},${sy+coneH} L ${sx+coneW},${sy+coneH} Z`}
              fill="#FEF0F0" opacity="0.5"/>
            <text x={sx+coneW+10} y={sy-40} fontSize="12" fill="#c0392b" fontWeight="700">Zone of</text>
            <text x={sx+coneW+10} y={sy-26} fontSize="12" fill="#c0392b" fontWeight="700">silence</text>
            <text x={sx+coneW+10} y={sy-12} fontSize="11" fill="#555">(undisturbed)</text>

            {/* Zone of action */}
            <text x={ox+15} y={sy-15} fontSize="12" fill="#9b59b6" fontWeight="700">Zone of action</text>
            <text x={ox+15} y={sy+2} fontSize="11" fill="#555">(affected by source)</text>

            {/* Source dot and velocity */}
            <circle cx={sx} cy={sy} r={6} fill="#c0392b"/>
            <line x1={sx-90} y1={sy+20} x2={sx-10} y2={sy+20} stroke="#27ae60" strokeWidth="2.5" markerEnd="url(#spArr)"/>
            <text x={sx-95} y={sy+38} fontSize="11" fill="#27ae60" fontWeight="700">U &gt; a</text>

            {/* Mach angle annotation */}
            <path d={`M ${sx+40},${sy} A 40 40 0 0 0 ${sx + 40*Math.cos(-mu)},${sy + 40*Math.sin(-mu)}`}
              fill="none" stroke="#e07b00" strokeWidth="1.5"/>
            <text x={sx+48} y={sy-12} fontSize="12" fill="#e07b00" fontWeight="700">μ</text>
            <text x={sx+48} y={sy-26} fontSize="11" fill="#e07b00">sin μ = 1/Ma</text>
            <text x={sx+48} y={sy-40} fontSize="11" fill="#e07b00">μ = 30° (Ma=2)</text>

            <rect x={ox+6} y={H-68} width={pw-12} height={44} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
            <text x={ox+12} y={H-52} fontSize="11" fill="#7B0D0D" fontWeight="700">All wavefronts trail behind source → Mach cone</text>
            <text x={ox+12} y={H-38} fontSize="11" fill="#444">Fluid has no warning → shock waves form</text>
            <text x={ox+12} y={H-24} fontSize="11" fill="#444">Cone half-angle: μ = arcsin(1/Ma)</text>
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>A moving source emits sound waves in all directions. At subsonic speeds, waves propagate ahead — the fluid is "warned" and adjusts smoothly (no shocks). At supersonic speeds, the source outruns its own waves; all disturbances are confined behind the Mach cone with half-angle μ = arcsin(1/Ma). Observers outside the cone hear nothing until the cone passes — the sonic boom. This is why supersonic flow cannot adjust upstream: information cannot travel forward faster than the flow.</div>
    </div>
  );
}

// ── Diagram 3: Normal Shock Wave ──────────────────────────────────
function DiagramNormalShock() {
  const W = 900, H = 320;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Normal Shock Wave — Upstream and Downstream Conditions</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="nsArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
          <marker id="nsArrG" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#27ae60"/>
          </marker>
          <marker id="nsArrO" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#e07b00"/>
          </marker>
        </defs>

        {/* Upstream region */}
        <rect x={10} y={10} width={360} height={280} rx={5} fill="#f0fff4" stroke="#27ae60" strokeWidth="1.5"/>
        <text x={190} y={32} textAnchor="middle" fontSize="14" fill="#145214" fontWeight="700">Upstream (State 1) — Supersonic</text>

        {/* Upstream streamlines */}
        {[60,100,140,180,220,260].map(y => (
          <line key={y} x1={20} y1={y} x2={365} y2={y}
            stroke="#27ae60" strokeWidth="1.6" opacity="0.6" markerEnd="url(#nsArrG)"/>
        ))}

        {/* Upstream labels */}
        <rect x={18} y={280} width={350} height={52} rx={3} fill="#e8f4e8" stroke="#27ae60" strokeWidth="1"/>
        <text x={24} y={298} fontSize="12" fill="#145214" fontWeight="700">Ma₁ &gt; 1  (supersonic)</text>
        <text x={24} y={314} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">Low p₁, low T₁, low ρ₁, high U₁</text>
        <text x={24} y={328} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">High total pressure p₀₁</text>

        {/* Shock wave — vertical band */}
        <rect x={370} y={10} width={18} height={280} fill="#c0392b" opacity="0.85"/>
        <text x={379} y={8} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">SHOCK</text>
        {/* Shock thickness annotation */}
        <line x1={392} y1={100} x2={420} y2={100} stroke="#c0392b" strokeWidth="1"/>
        <text x={422} y={104} fontSize="10" fill="#c0392b">~10⁻⁷ m thick</text>
        <text x={422} y={118} fontSize="10" fill="#c0392b">(a few mean free paths)</text>

        {/* Downstream region */}
        <rect x={392} y={10} width={498} height={280} rx={5} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={640} y={32} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Downstream (State 2) — Subsonic</text>

        {/* Downstream streamlines — compressed together */}
        {[60,100,140,180,220,260].map(y => (
          <line key={y} x1={395} y1={y} x2={882} y2={y}
            stroke="#c0392b" strokeWidth="1.6" opacity="0.55" markerEnd="url(#nsArr)"/>
        ))}

        {/* Downstream labels */}
        <rect x={400} y={280} width={482} height={52} rx={3} fill="#fff5f5" stroke="#c0392b" strokeWidth="1"/>
        <text x={406} y={298} fontSize="12" fill="#7B0D0D" fontWeight="700">Ma₂ &lt; 1  (subsonic)</text>
        <text x={406} y={314} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">High p₂, high T₂, high ρ₂, low U₂</text>
        <text x={406} y={328} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">Lower total pressure p₀₂ &lt; p₀₁ (entropy rises)</text>

        {/* Rankine-Hugoniot relations panel */}
        <rect x={530} y={42} width={350} height={210} rx={4} fill="#fff" stroke="#f0cece"/>
        <text x={705} y={62} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Rankine–Hugoniot Relations</text>
        {[
          { y:80,  bg:'#fff5f5', bc:'#c0392b', rows:['Density ratio:','ρ₂/ρ₁ = (γ+1)Ma₁²/ [(γ−1)Ma₁²+2]'] },
          { y:118, bg:'#fff5e6', bc:'#e07b00', rows:['Pressure ratio:','p₂/p₁ = [2γMa₁²−(γ−1)]/(γ+1)'] },
          { y:156, bg:'#f0fff4', bc:'#27ae60', rows:['Temperature ratio:','T₂/T₁ = p₂ρ₁/(p₁ρ₂)'] },
          { y:194, bg:'#f5e6ff', bc:'#9b59b6', rows:['Downstream Mach:','Ma₂² = [(γ−1)Ma₁²+2]/[2γMa₁²−(γ−1)]'] },
          { y:232, bg:'#FEF0F0', bc:'#c0392b', rows:['Total pressure loss:','p₀₂/p₀₁ < 1  (irreversible — entropy increases)'] },
        ].map(({y,bg,bc,rows}) => (
          <g key={y}>
            <rect x={536} y={y} width={337} height={33} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={542} y={y+13} fontSize="11" fill="#333" fontWeight="700">{rows[0]}</text>
            <text x={542} y={y+27} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">{rows[1]}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>A normal shock wave is an infinitesimally thin discontinuity (a few molecular mean-free-paths) standing perpendicular to the flow. Across it, the flow abruptly decelerates from supersonic to subsonic; density, pressure and temperature all jump; total pressure decreases (entropy increases — the process is irreversible). The Rankine–Hugoniot relations (conservation of mass, momentum, energy) fully determine all downstream quantities from upstream Mach number and γ.</div>
    </div>
  );
}

// ── Diagram 4: Oblique Shock & Prandtl-Meyer Expansion ────────────
function DiagramObliqueShock() {
  const W = 900, H = 320;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Oblique Shock Wave vs Prandtl–Meyer Expansion Fan</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="obArr" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#c0392b"/>
          </marker>
          <marker id="obArrG" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#27ae60"/>
          </marker>
          <marker id="obArrB" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#2E75B6"/>
          </marker>
        </defs>

        {/* ── Left panel: Oblique shock on a wedge ── */}
        <rect x={10} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={10+PW/2} y={30} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Oblique Shock — Compression Corner</text>

        {(()=>{
          const ox = 10, w = PW;
          const wallY = 200;
          const wedgeAngle = 18 * Math.PI / 180;  // θ = 18°
          const shockAngle = 42 * Math.PI / 180;  // β ≈ 42° for Ma=2.5, θ=18°

          // Wedge surface
          const wedgeLen = 250;
          const wedgeTipX = 90, wedgeTipY = wallY;
          const wedgeEndX = wedgeTipX + wedgeLen * Math.cos(wedgeAngle);
          const wedgeEndY = wedgeTipY - wedgeLen * Math.sin(wedgeAngle);

          // Shock line from tip
          const shockLen = 220;
          const shockEndX = wedgeTipX + shockLen * Math.cos(-shockAngle + Math.PI);
          const shockEndY = wedgeTipY + shockLen * Math.sin(-shockAngle + Math.PI);
          // shock goes up-left from tip
          const shockX2 = wedgeTipX - shockLen * Math.cos(shockAngle - Math.PI/2 + Math.PI/2);
          const shockY2 = wedgeTipY - shockLen * Math.sin(shockAngle);

          return <>
            {/* Incoming supersonic streamlines */}
            {[60,90,120,150,175].map(y => (
              <line key={y} x1={ox+15} y1={y} x2={wedgeTipX} y2={y}
                stroke="#27ae60" strokeWidth="1.5" opacity="0.6" markerEnd="url(#obArrG)"/>
            ))}

            {/* Shock wave line */}
            <line x1={wedgeTipX} y1={wedgeTipY}
              x2={wedgeTipX - 200 * Math.cos(Math.PI/2 - shockAngle)}
              y2={wedgeTipY - 200 * Math.sin(Math.PI/2 - shockAngle + shockAngle)}
              stroke="#c0392b" strokeWidth="3"/>
            {/* Approximate shock: 42° from horizontal */}
            <line x1={wedgeTipX} y1={wedgeTipY}
                  x2={wedgeTipX - 180} y2={wedgeTipY - 162}
              stroke="#c0392b" strokeWidth="3"/>
            <text x={wedgeTipX - 148} y={wedgeTipY - 172} fontSize="12" fill="#c0392b" fontWeight="700">Oblique shock</text>

            {/* Post-shock streamlines — deflected to follow wedge */}
            {[60,90,120,150,175].map((y,i) => {
              const xStart = wedgeTipX + (y - 60) * 0.5;
              return (
                <line key={y}
                  x1={xStart} y1={y}
                  x2={ox+w-15} y2={y + 25 + i*2}
                  stroke="#2E75B6" strokeWidth="1.5" opacity="0.6" markerEnd="url(#obArrB)"/>
              );
            })}

            {/* Wedge body */}
            <polygon
              points={`${wedgeTipX},${wedgeTipY} ${wedgeTipX+250},${wedgeTipY-82} ${wedgeTipX+250},${wedgeTipY}`}
              fill="#ccc" stroke="#555" strokeWidth="1.5"/>
            <text x={wedgeTipX+130} y={wedgeTipY+15} fontSize="12" fill="#555" fontWeight="700">Wedge (θ=18°)</text>

            {/* Angle annotations */}
            <path d={`M ${wedgeTipX+50},${wedgeTipY} A 50 50 0 0 0 ${wedgeTipX+50-22},${wedgeTipY-18}`}
              fill="none" stroke="#e07b00" strokeWidth="1.5"/>
            <text x={wedgeTipX+58} y={wedgeTipY-8} fontSize="11" fill="#e07b00" fontWeight="700">θ=18°</text>

            {/* shock angle */}
            <text x={wedgeTipX-120} y={wedgeTipY-30} fontSize="11" fill="#c0392b">β ≈ 42°</text>
            <text x={wedgeTipX-120} y={wedgeTipY-16} fontSize="10" fill="#c0392b">shock angle</text>

            {/* Labels */}
            <text x={ox+20} y={55} fontSize="11" fill="#27ae60" fontWeight="700">Ma₁ &gt; 1</text>
            <text x={ox+20} y={69} fontSize="10" fill="#27ae60">(supersonic)</text>
            <text x={ox+w-100} y={55} fontSize="11" fill="#2E75B6" fontWeight="700">Ma₂, deflected</text>
            <text x={ox+w-100} y={69} fontSize="10" fill="#2E75B6">(still supersonic)</text>

            <rect x={ox+8} y={H-68} width={PW-16} height={44} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
            <text x={ox+14} y={H-52} fontSize="11" fill="#7B0D0D" fontWeight="700">θ–β–Ma relation: tan θ = 2 cot β · (Ma₁²sin²β−1)/(Ma₁²(γ+cosβ)+2)</text>
            <text x={ox+14} y={H-38} fontSize="11" fill="#444">Oblique shock is weaker than normal shock; flow stays supersonic</text>
            <text x={ox+14} y={H-24} fontSize="11" fill="#444">For θ &gt; θ_max: shock detaches → bow shock forms</text>
          </>;
        })()}

        {/* ── Right panel: Expansion fan ── */}
        <rect x={PW+20} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={PW+20+PW/2} y={30} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Prandtl–Meyer Expansion Fan</text>

        {(()=>{
          const ox = PW + 20, w = PW;
          const cornerX = ox + 200, cornerY = 200;
          const turnAngle = 20 * Math.PI / 180; // expansion angle 20°

          // Fan rays from corner
          const numRays = 8;
          return <>
            {/* Incoming streamlines */}
            {[60,90,120,150,175].map(y => (
              <line key={y} x1={ox+15} y1={y} x2={cornerX} y2={y}
                stroke="#27ae60" strokeWidth="1.5" opacity="0.6" markerEnd="url(#obArrG)"/>
            ))}

            {/* Upper wall (horizontal then turns down) */}
            <line x1={ox+15} y1={cornerY} x2={cornerX} y2={cornerY}
              stroke="#555" strokeWidth="2.5"/>
            <line x1={cornerX} y1={cornerY}
              x2={cornerX + 220 * Math.cos(turnAngle)}
              y2={cornerY + 220 * Math.sin(turnAngle)}
              stroke="#555" strokeWidth="2.5"/>

            {/* Expansion fan rays */}
            {Array.from({length:numRays+1}, (_,i) => {
              const angle = (i / numRays) * turnAngle;
              const len = 160 + i * 5;
              return (
                <line key={i}
                  x1={cornerX} y1={cornerY}
                  x2={cornerX + len * Math.cos(angle)}
                  y2={cornerY - len * Math.sin(angle) + len * Math.sin(angle) + len * Math.sin(angle)}
                  stroke="#9b59b6" strokeWidth="1.3" opacity={0.5 + i*0.04}
                  strokeDasharray="5,3"/>
              );
            })}

            {/* Better fan rays — emanating rightward-downward from corner */}
            {Array.from({length:9}, (_,i) => {
              const startAngle = -5 * Math.PI/180;
              const endAngle = turnAngle + 5 * Math.PI/180;
              const angle = startAngle + i*(endAngle-startAngle)/8;
              const len = 150;
              return (
                <line key={`fan${i}`}
                  x1={cornerX} y1={cornerY}
                  x2={cornerX + len * Math.cos(angle)}
                  y2={cornerY + len * Math.sin(angle)}
                  stroke="#9b59b6" strokeWidth="1.4" opacity="0.6"/>
              );
            })}
            <text x={cornerX+80} y={cornerY-20} fontSize="12" fill="#9b59b6" fontWeight="700">Expansion fan</text>
            <text x={cornerX+80} y={cornerY-6} fontSize="11" fill="#9b59b6">(Mach waves)</text>

            {/* Post-expansion streamlines */}
            {[60,90,120,150,175].map((y,i) => (
              <line key={y}
                x1={cornerX} y1={y}
                x2={ox+w-15} y2={y + 18 + i*3}
                stroke="#2E75B6" strokeWidth="1.5" opacity="0.6" markerEnd="url(#obArrB)"/>
            ))}

            {/* Pressure/speed labels */}
            <rect x={ox+15} y={60} width={120} height={50} rx={3} fill="#e8f4e8" stroke="#27ae60" strokeWidth="1"/>
            <text x={ox+21} y={76} fontSize="11" fill="#145214" fontWeight="700">Region 1 (upstream)</text>
            <text x={ox+21} y={90} fontSize="10" fill="#444">Ma₁ &gt; 1, p₁, T₁</text>
            <text x={ox+21} y={104} fontSize="10" fill="#444">Higher p, lower Ma</text>

            <rect x={ox+w-145} y={60} width={130} height={50} rx={3} fill="#f5e6ff" stroke="#9b59b6" strokeWidth="1"/>
            <text x={ox+w-139} y={76} fontSize="11" fill="#5b2c8d" fontWeight="700">Region 2 (downstream)</text>
            <text x={ox+w-139} y={90} fontSize="10" fill="#444">Ma₂ &gt; Ma₁  (faster!)</text>
            <text x={ox+w-139} y={104} fontSize="10" fill="#444">Lower p, T — isentropic</text>

            <rect x={ox+8} y={H-68} width={PW-16} height={44} rx={3} fill="#f5e6ff" stroke="#9b59b6" strokeWidth="1"/>
            <text x={ox+14} y={H-52} fontSize="11" fill="#5b2c8d" fontWeight="700">Prandtl–Meyer function: ν(Ma) = √((γ+1)/(γ−1))·atan(√((γ−1)(Ma²−1)/(γ+1))) − atan(√(Ma²−1))</text>
            <text x={ox+14} y={H-38} fontSize="11" fill="#444">ν₂ = ν₁ + θ  (θ = turning angle)</text>
            <text x={ox+14} y={H-24} fontSize="11" fill="#444">Expansion is isentropic — total pressure conserved (no shock losses)</text>
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>When supersonic flow turns into itself (compression), an oblique shock forms — a thin discontinuity across which pressure rises, Mach number drops but remains supersonic, and entropy increases. When supersonic flow turns away from itself (expansion), a smooth isentropic fan of Mach waves forms — the Prandtl–Meyer expansion fan — across which Mach number increases, pressure and temperature drop, and total pressure is conserved. These two phenomena govern all supersonic aerodynamics.</div>
    </div>
  );
}

// ── Diagram 5: de Laval Nozzle — Converging–Diverging ─────────────
function DiagramNozzle() {
  const W = 900, H = 340;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Converging–Diverging (de Laval) Nozzle — Flow Regimes</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="nzArr" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#c0392b"/>
          </marker>
          <marker id="nzArrG" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#27ae60"/>
          </marker>
        </defs>

        {/* Nozzle walls — converging then diverging */}
        {(()=>{
          // Throat at x=440
          const throatX = 440, inletX = 60, exitX = 820;
          const throatY_top = 125, throatY_bot = 215;
          const inletY_top = 60, inletY_bot = 280;
          const exitY_top = 80, exitY_bot = 260;

          // Upper wall
          const upperWall = `M ${inletX},${inletY_top} C ${200},${inletY_top} ${320},${throatY_top} ${throatX},${throatY_top} C ${560},${throatY_top} ${680},${exitY_top} ${exitX},${exitY_top}`;
          // Lower wall
          const lowerWall = `M ${inletX},${inletY_bot} C ${200},${inletY_bot} ${320},${throatY_bot} ${throatX},${throatY_bot} C ${560},${throatY_bot} ${680},${exitY_bot} ${exitX},${exitY_bot}`;

          return <>
            {/* Nozzle fill */}
            <path d={upperWall + ` L ${exitX},${exitY_bot} C ${680},${exitY_bot} ${560},${throatY_bot} ${throatX},${throatY_bot} C ${320},${throatY_bot} ${200},${inletY_bot} ${inletX},${inletY_bot} Z`}
              fill="#e8c4c4" opacity="0.35"/>
            <path d={upperWall} fill="none" stroke="#555" strokeWidth="3"/>
            <path d={lowerWall} fill="none" stroke="#555" strokeWidth="3"/>

            {/* Throat marker */}
            <line x1={throatX} y1={throatY_top-10} x2={throatX} y2={throatY_bot+10}
              stroke="#c0392b" strokeWidth="1.5" strokeDasharray="4,3"/>
            <text x={throatX} y={throatY_top-16} textAnchor="middle" fontSize="12" fill="#c0392b" fontWeight="700">Throat (A*)</text>
            <text x={throatX} y={throatY_top-2} textAnchor="middle" fontSize="11" fill="#c0392b">Ma = 1</text>

            {/* Section labels */}
            <text x={240} y={44} textAnchor="middle" fontSize="13" fill="#27ae60" fontWeight="700">Converging Section</text>
            <text x={240} y={58} textAnchor="middle" fontSize="11" fill="#27ae60">Subsonic accelerates</text>
            <text x={640} y={44} textAnchor="middle" fontSize="13" fill="#9b59b6" fontWeight="700">Diverging Section</text>
            <text x={640} y={58} textAnchor="middle" fontSize="11" fill="#9b59b6">Supersonic accelerates</text>

            {/* Flow streamlines inside — subsonic part */}
            {[155,170,185,200].map((y,i) => (
              <line key={y} x1={70} y1={y} x2={throatX-2} y2={170}
                stroke="#27ae60" strokeWidth="1.4" opacity="0.55" markerEnd="url(#nzArrG)"/>
            ))}
            {/* Flow streamlines inside — supersonic part */}
            {[135,155,185,205].map((y,i) => (
              <line key={y} x1={throatX+2} y1={170} x2={810} y2={y}
                stroke="#9b59b6" strokeWidth="1.4" opacity="0.55" markerEnd="url(#nzArr)"/>
            ))}

            {/* Ma profile curve */}
            <text x={80} y={300} fontSize="12" fill="#333" fontWeight="700">Ma profile along nozzle:</text>

            {/* Draw approximate Ma curve */}
            <path d="M 60,320 C 150,315 280,310 440,272 C 560,235 680,220 820,200"
              fill="none" stroke="#c0392b" strokeWidth="2.5"/>
            {/* Reference Ma=1 line */}
            <line x1={60} y1={272} x2={820} y2={272}
              stroke="#555" strokeWidth="1" strokeDasharray="4,3" opacity="0.5"/>
            <text x={825} y={276} fontSize="10" fill="#555">Ma=1</text>

            {/* Axis for Ma curve */}
            <line x1={60} y1={330} x2={820} y2={330} stroke="#333" strokeWidth="1.5"/>
            <text x={825} y={334} fontSize="10" fill="#333">x</text>
            <text x={32} y={276} fontSize="10" fill="#333">1.0</text>
            <text x={32} y={322} fontSize="10" fill="#333">0</text>

            {/* p/p₀ curve */}
            <path d="M 60,310 C 150,308 280,304 440,290 C 560,278 680,270 820,265"
              fill="none" stroke="#e07b00" strokeWidth="2" strokeDasharray="6,3"/>
            <text x={825} y={268} fontSize="10" fill="#e07b00">p/p₀</text>

            {/* Normal shock in diverging section (off-design) */}
            <rect x={580} y={134} width={8} height={72} fill="#c0392b" opacity="0.8"/>
            <text x={588} y={130} fontSize="10" fill="#c0392b">Normal</text>
            <text x={588} y={142} fontSize="10" fill="#c0392b">shock</text>
            <text x={588} y={154} fontSize="10" fill="#c0392b">(off-design)</text>

            {/* Inlet conditions */}
            <rect x={18} y={130} width={88} height={54} rx={3} fill="#e8f4e8" stroke="#27ae60" strokeWidth="1"/>
            <text x={24} y={146} fontSize="11" fill="#145214" fontWeight="700">Reservoir</text>
            <text x={24} y={160} fontSize="10" fill="#444">p₀, T₀</text>
            <text x={24} y={174} fontSize="10" fill="#444">Ma ≈ 0</text>

            {/* Exit conditions */}
            <rect x={794} y={130} width={96} height={70} rx={3} fill="#f5e6ff" stroke="#9b59b6" strokeWidth="1"/>
            <text x={800} y={147} fontSize="11" fill="#5b2c8d" fontWeight="700">Exit (design)</text>
            <text x={800} y={162} fontSize="10" fill="#444">Ma &gt; 1</text>
            <text x={800} y={176} fontSize="10" fill="#444">p_e = p_a</text>
            <text x={800} y={190} fontSize="10" fill="#444">(no shocks)</text>
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>A converging–diverging (de Laval) nozzle accelerates a subsonic flow to supersonic by exploiting the area–Mach relationship: for subsonic flow, decreasing area increases speed (like a venturi); for supersonic flow, increasing area increases speed. The throat reaches exactly Ma = 1 at design conditions. If back pressure is too high, a normal shock appears in the diverging section, returning flow to subsonic. Rocket engines and wind tunnels use this geometry to generate controlled supersonic flows.</div>
    </div>
  );
}

// ── Diagram 6: Bow Shock & Detached Shock ─────────────────────────
function DiagramBowShock() {
  const W = 900, H = 320;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Attached Oblique Shock vs Detached Bow Shock</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="bsArr" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#27ae60"/>
          </marker>
          <marker id="bsArrR" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#c0392b"/>
          </marker>
          <marker id="bsArrB" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#2E75B6"/>
          </marker>
        </defs>

        {/* Left: Sharp wedge — attached shock */}
        <rect x={10} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={10+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Sharp Wedge — Attached Oblique Shock</text>

        {(()=>{
          const tipX = 160, tipY = 160;
          const wedgeH = 100, wedgeL = 220;
          const shockAngle = 40 * Math.PI/180;
          return <>
            {/* Incoming streamlines */}
            {[60,90,120,145,175,205,235,260].map(y => (
              <line key={y} x1={18} y1={y} x2={y < 160 ? tipX - (tipY - y)/Math.tan(shockAngle) : tipX}
                y2={y < 160 ? y : y}
                stroke="#27ae60" strokeWidth="1.4" opacity="0.55" markerEnd="url(#bsArr)"/>
            ))}

            {/* Shock lines from tip — upper and lower */}
            <line x1={tipX} y1={tipY}
              x2={tipX - 150 * Math.sin(shockAngle)}
              y2={tipY - 150 * Math.cos(shockAngle)}
              stroke="#c0392b" strokeWidth="2.5"/>
            <line x1={tipX} y1={tipY}
              x2={tipX - 150 * Math.sin(shockAngle)}
              y2={tipY + 150 * Math.cos(shockAngle)}
              stroke="#c0392b" strokeWidth="2.5"/>

            {/* Wedge body */}
            <polygon points={`${tipX},${tipY} ${tipX+wedgeL},${tipY-wedgeH} ${tipX+wedgeL},${tipY+wedgeH}`}
              fill="#ccc" stroke="#555" strokeWidth="1.5"/>
            <text x={tipX+wedgeL/2+10} y={tipY+5} textAnchor="middle" fontSize="12" fill="#555" fontWeight="700">Sharp wedge</text>
            <text x={tipX+wedgeL/2+10} y={tipY+19} textAnchor="middle" fontSize="11" fill="#555">θ &lt; θ_max</text>

            {/* Post-shock deflected streamlines */}
            {[60,90,120].map((y,i) => (
              <line key={y} x1={tipX} y1={y}
                x2={PW+6} y2={y + 30}
                stroke="#2E75B6" strokeWidth="1.3" opacity="0.5" markerEnd="url(#bsArrB)"/>
            ))}
            {[175,205,235,260].map((y,i) => (
              <line key={y} x1={tipX} y1={y}
                x2={PW+6} y2={y - 25 + i*5}
                stroke="#2E75B6" strokeWidth="1.3" opacity="0.5" markerEnd="url(#bsArrB)"/>
            ))}

            {/* β and θ labels */}
            <text x={100} y={108} fontSize="11" fill="#c0392b">β (shock)</text>
            <text x={174} y={130} fontSize="11" fill="#e07b00">θ (wedge)</text>

            <rect x={16} y={H-62} width={PW-12} height={38} rx={3} fill="#e8f4e8" stroke="#27ae60" strokeWidth="1"/>
            <text x={22} y={H-46} fontSize="11" fill="#145214" fontWeight="700">Shock attached when θ ≤ θ_max(Ma)</text>
            <text x={22} y={H-30} fontSize="11" fill="#444">θ_max ≈ 45° for Ma→∞; weaker for lower Ma</text>
          </>;
        })()}

        {/* Right: Blunt body — detached bow shock */}
        <rect x={PW+20} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={PW+20+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Blunt Body — Detached Bow Shock</text>

        {(()=>{
          const ox = PW+20;
          const bodyX = ox + 310, bodyY = 160, bodyR = 55;

          return <>
            {/* Incoming streamlines */}
            {[60,90,120,145,175,205,235,260].map(y => (
              <line key={y} x1={ox+12} y1={y} x2={ox+150} y2={y}
                stroke="#27ae60" strokeWidth="1.4" opacity="0.55" markerEnd="url(#bsArr)"/>
            ))}

            {/* Bow shock — curved */}
            <path d={`M ${bodyX-110},${bodyY-150} C ${bodyX-130},${bodyY-80} ${bodyX-140},${bodyY-30} ${bodyX-145},${bodyY} C ${bodyX-140},${bodyY+30} ${bodyX-130},${bodyY+80} ${bodyX-110},${bodyY+150}`}
              fill="none" stroke="#c0392b" strokeWidth="3"/>
            <text x={bodyX-185} y={bodyY-10} fontSize="12" fill="#c0392b" fontWeight="700">Bow shock</text>
            <text x={bodyX-185} y={bodyY+6} fontSize="11" fill="#c0392b">(detached)</text>

            {/* Stagnation streamline — normal shock at centreline */}
            <line x1={ox+12} y1={bodyY} x2={bodyX-146} y2={bodyY}
              stroke="#27ae60" strokeWidth="1.8" markerEnd="url(#bsArr)"/>
            <line x1={bodyX-144} y1={bodyY} x2={bodyX-bodyR-2} y2={bodyY}
              stroke="#c0392b" strokeWidth="1.5" strokeDasharray="3,2"/>

            {/* Normal shock annotation at centreline */}
            <text x={bodyX-143} y={bodyY-8} fontSize="10" fill="#c0392b">Normal</text>
            <text x={bodyX-143} y={bodyY+18} fontSize="10" fill="#c0392b">shock here</text>
            <text x={bodyX-143} y={bodyY+30} fontSize="10" fill="#c0392b">(Ma→subsonic)</text>

            {/* Curved bow shock lines above/below */}
            {[70,100,130].map((dy,i) => (
              <path key={dy}
                d={`M ${bodyX-110},${bodyY-dy} C ${bodyX-80},${bodyY-dy+10} ${bodyX-50},${bodyY-dy+20} ${ox+PW+8},${bodyY-dy+30}`}
                fill="none" stroke="#2E75B6" strokeWidth="1.3" opacity="0.5" markerEnd="url(#bsArrB)"/>
            ))}
            {[70,100,130].map((dy,i) => (
              <path key={dy}
                d={`M ${bodyX-110},${bodyY+dy} C ${bodyX-80},${bodyY+dy-10} ${bodyX-50},${bodyY+dy-20} ${ox+PW+8},${bodyY+dy-30}`}
                fill="none" stroke="#2E75B6" strokeWidth="1.3" opacity="0.5" markerEnd="url(#bsArrB)"/>
            ))}

            {/* Blunt body */}
            <circle cx={bodyX} cy={bodyY} r={bodyR} fill="#ccc" stroke="#555" strokeWidth="1.5"/>
            <text x={bodyX} y={bodyY+5} textAnchor="middle" fontSize="12" fill="#555" fontWeight="700">Blunt</text>
            <text x={bodyX} y={bodyY+19} textAnchor="middle" fontSize="11" fill="#555">body</text>

            {/* Subsonic pocket */}
            <text x={bodyX-bodyR-60} y={bodyY-20} fontSize="11" fill="#e07b00" fontWeight="700">Subsonic</text>
            <text x={bodyX-bodyR-60} y={bodyY-6} fontSize="11" fill="#e07b00">pocket</text>

            <rect x={ox+8} y={H-62} width={PW-12} height={38} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
            <text x={ox+14} y={H-46} fontSize="11" fill="#7B0D0D" fontWeight="700">Always forms on blunt bodies (all Ma &gt; 1)</text>
            <text x={ox+14} y={H-30} fontSize="11" fill="#444">Generates large entropy rise → high wave drag</text>
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>For sharp wedges with half-angle below θ_max, an oblique shock attaches to the tip. If the wedge angle exceeds θ_max, or the body is blunt, the shock detaches and curves ahead of the body — the bow shock. At the centreline stagnation streamline, the bow shock is locally normal; this subsonic pocket behind it explains why blunt re-entry capsules can be controlled and why they generate intense aerodynamic heating. Sharp-nosed vehicles minimise bow shock stand-off distance and wave drag.</div>
    </div>
  );
}

// ── Diagram 7: Isentropic Flow — Area–Mach Relation & p/p₀ ────────
function DiagramIsentropic() {
  const W = 900, H = 300;

  // Isentropic relations: p/p0 = (1 + (γ-1)/2 * Ma²)^(-γ/(γ-1))
  // T/T0 = (1 + (γ-1)/2 * Ma²)^(-1)
  // ρ/ρ0 = (1 + (γ-1)/2 * Ma²)^(-1/(γ-1))
  // A/A* = (1/Ma) * ((2/(γ+1))*(1 + (γ-1)/2 * Ma²))^((γ+1)/(2*(γ-1)))
  const gamma = 1.4;
  const pts = [];
  for (let Ma = 0.1; Ma <= 4.0; Ma += 0.05) {
    const factor = 1 + (gamma-1)/2 * Ma*Ma;
    const p  = Math.pow(factor, -gamma/(gamma-1));
    const T  = Math.pow(factor, -1);
    const rho= Math.pow(factor, -1/(gamma-1));
    const A  = (1/Ma) * Math.pow((2/(gamma+1))*factor, (gamma+1)/(2*(gamma-1)));
    pts.push({Ma, p, T, rho, A});
  }

  // Map Ma [0,4] → x [80, 820], ratio [0,1] → y [260, 40]
  const mx = (Ma) => 80 + (Ma/4.0)*740;
  const my = (v, vmin, vmax) => 260 - ((v - vmin)/(vmax - vmin))*200;

  // Build SVG path strings
  const pPath  = pts.map(({Ma,p},  i) => `${i===0?'M':'L'} ${mx(Ma)},${my(p,0,1)}`).join(' ');
  const TPath  = pts.map(({Ma,T},  i) => `${i===0?'M':'L'} ${mx(Ma)},${my(T,0,1)}`).join(' ');
  const rPath  = pts.map(({Ma,rho},i) => `${i===0?'M':'L'} ${mx(Ma)},${my(rho,0,1)}`).join(' ');

  // A/A* has large range — map 0→10
  const APath  = pts.filter(({A})=>A<8).map(({Ma,A},i,arr) => `${i===0?'M':'L'} ${mx(Ma)},${my(A,0,8)}`).join(' ');

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Isentropic Flow Relations — p/p₀, T/T₀, ρ/ρ₀ and A/A* vs Mach Number</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        {/* Axes */}
        <line x1={80} y1={260} x2={830} y2={260} stroke="#333" strokeWidth="1.5"/>
        <line x1={80} y1={260} x2={80}  y2={25}  stroke="#333" strokeWidth="1.5"/>

        {/* X axis ticks */}
        {[0,0.5,1,1.5,2,2.5,3,3.5,4].map(ma => (
          <g key={ma}>
            <line x1={mx(ma)} y1={257} x2={mx(ma)} y2={263} stroke="#333" strokeWidth="1.2"/>
            <text x={mx(ma)} y={275} textAnchor="middle" fontSize="11" fill="#555">{ma}</text>
          </g>
        ))}
        <text x={460} y={292} textAnchor="middle" fontSize="13" fill="#333" fontWeight="700">Mach Number (Ma)</text>

        {/* Y axis ticks */}
        {[0, 0.2, 0.4, 0.6, 0.8, 1.0].map(v => (
          <g key={v}>
            <line x1={77} y1={my(v,0,1)} x2={83} y2={my(v,0,1)} stroke="#333" strokeWidth="1.2"/>
            <text x={72} y={my(v,0,1)+4} textAnchor="end" fontSize="11" fill="#555">{v.toFixed(1)}</text>
          </g>
        ))}

        {/* Sonic line */}
        <line x1={mx(1)} y1={260} x2={mx(1)} y2={25}
          stroke="#c0392b" strokeWidth="1.5" strokeDasharray="5,4" opacity="0.6"/>
        <text x={mx(1)+4} y={38} fontSize="11" fill="#c0392b" fontWeight="700">Ma = 1</text>

        {/* Data curves */}
        <path d={pPath}  fill="none" stroke="#c0392b" strokeWidth="2.5"/>
        <path d={TPath}  fill="none" stroke="#e07b00" strokeWidth="2.5"/>
        <path d={rPath}  fill="none" stroke="#9b59b6" strokeWidth="2.5"/>

        {/* A/A* on secondary scale — right axis */}
        <line x1={820} y1={260} x2={820} y2={25} stroke="#27ae60" strokeWidth="1.5" opacity="0.5"/>
        {[0,2,4,6,8].map(v => (
          <g key={v}>
            <line x1={817} y1={my(v,0,8)} x2={823} y2={my(v,0,8)} stroke="#27ae60" strokeWidth="1.2"/>
            <text x={826} y={my(v,0,8)+4} fontSize="11" fill="#27ae60">{v}</text>
          </g>
        ))}
        <text x={858} y={140} fontSize="11" fill="#27ae60" transform="rotate(90,858,140)">A/A*</text>
        <path d={APath}  fill="none" stroke="#27ae60" strokeWidth="2.5"/>

        {/* Legend */}
        <rect x={110} y={30} width={220} height={100} rx={4} fill="#fff" stroke="#f0cece"/>
        {[
          { color:'#c0392b', label:'p/p₀  (pressure ratio)' },
          { color:'#e07b00', label:'T/T₀  (temperature ratio)' },
          { color:'#9b59b6', label:'ρ/ρ₀  (density ratio)' },
          { color:'#27ae60', label:'A/A*  (area ratio, right axis)' },
        ].map(({color,label},i) => (
          <g key={i}>
            <line x1={118} y1={48+i*22} x2={140} y2={48+i*22} stroke={color} strokeWidth="2.5"/>
            <text x={146} y={52+i*22} fontSize="11" fill="#333">{label}</text>
          </g>
        ))}

        {/* Annotations at Ma=2 */}
        {(()=>{
          const ma = 2.0;
          const factor = 1 + (gamma-1)/2 * ma*ma;
          const p  = Math.pow(factor, -gamma/(gamma-1)).toFixed(3);
          const T  = Math.pow(factor, -1).toFixed(3);
          const rho= Math.pow(factor, -1/(gamma-1)).toFixed(3);
          const A  = ((1/ma) * Math.pow((2/(gamma+1))*factor, (gamma+1)/(2*(gamma-1)))).toFixed(3);
          return (
            <g>
              <line x1={mx(ma)} y1={260} x2={mx(ma)} y2={30} stroke="#555" strokeWidth="1" strokeDasharray="3,3" opacity="0.5"/>
              <rect x={mx(ma)+6} y={50} width={135} height={85} rx={3} fill="#fffafa" stroke="#f0cece"/>
              <text x={mx(ma)+12} y={66} fontSize="11" fill="#555" fontWeight="700">At Ma = 2.0:</text>
              <text x={mx(ma)+12} y={81} fontSize="11" fill="#c0392b" fontFamily="'Courier New',monospace">p/p₀  = {p}</text>
              <text x={mx(ma)+12} y={96} fontSize="11" fill="#e07b00" fontFamily="'Courier New',monospace">T/T₀  = {T}</text>
              <text x={mx(ma)+12} y={111} fontSize="11" fill="#9b59b6" fontFamily="'Courier New',monospace">ρ/ρ₀  = {rho}</text>
              <text x={mx(ma)+12} y={126} fontSize="11" fill="#27ae60" fontFamily="'Courier New',monospace">A/A*  = {A}</text>
            </g>
          );
        })()}
      </svg>
      <div style={S.dCaption}>For isentropic (adiabatic, reversible) flow, all thermodynamic properties are uniquely determined by the local Mach number relative to the stagnation (total) conditions. As Mach number increases from 0 to 4, static pressure, temperature, and density all fall monotonically from their stagnation values. The area ratio A/A* has a minimum at Ma = 1 (the throat), then increases in both directions — which is why both subsonic and supersonic flows can exist in a diverging duct depending on upstream conditions.</div>
    </div>
  );
}

// ── Diagram 8: Shock Tube & Applications ──────────────────────────
function DiagramShockTube() {
  const W = 900, H = 300;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Shock Tube — Generating Shock Waves in the Laboratory</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="stArr" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#c0392b"/>
          </marker>
          <marker id="stArrL" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#2E75B6"/>
          </marker>
        </defs>

        {/* Tube walls */}
        <rect x={30} y={60} width={840} height={8} rx={2} fill="#555"/>
        <rect x={30} y={222} width={840} height={8} rx={2} fill="#555"/>

        {/* High-pressure driver section (left) */}
        <rect x={30} y={68} width={280} height={154} fill="#FEF0F0"/>
        <text x={170} y={130} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Driver (High p)</text>
        <text x={170} y={148} textAnchor="middle" fontSize="12" fill="#c0392b">Region 4</text>
        <text x={170} y={165} textAnchor="middle" fontSize="11" fill="#444">He or H₂ gas</text>
        <text x={170} y={181} textAnchor="middle" fontSize="11" fill="#444">p₄ &gt;&gt; p₁</text>

        {/* Diaphragm */}
        <rect x={308} y={62} width={10} height={162} fill="#c0392b"/>
        <text x={313} y={56} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">Diaphragm</text>
        <text x={313} y={246} textAnchor="middle" fontSize="10" fill="#c0392b">(bursts)</text>

        {/* Low-pressure driven section (right) */}
        <rect x={318} y={68} width={552} height={154} fill="#e8f4e8"/>
        <text x={594} y={130} textAnchor="middle" fontSize="14" fill="#145214" fontWeight="700">Driven (Low p)</text>
        <text x={594} y={148} textAnchor="middle" fontSize="12" fill="#27ae60">Region 1  (test gas)</text>
        <text x={594} y={165} textAnchor="middle" fontSize="11" fill="#444">Air or test gas at rest</text>
        <text x={594} y={181} textAnchor="middle" fontSize="11" fill="#444">p₁ (low), T₁</text>

        {/* After burst — wave diagram */}
        {/* Shock wave moving right */}
        <rect x={620} y={62} width={8} height={162} fill="#c0392b" opacity="0.85"/>
        <line x1={624} y1={58} x2={720} y2={58} stroke="#c0392b" strokeWidth="2" markerEnd="url(#stArr)"/>
        <text x={722} y={62} fontSize="12" fill="#c0392b" fontWeight="700">Shock (W_s)</text>
        <text x={722} y={76} fontSize="11" fill="#c0392b">moves right</text>

        {/* Contact surface moving right (slower) */}
        <rect x={470} y={62} width={5} height={162} fill="#e07b00" opacity="0.85"/>
        <line x1={472} y1={245} x2={560} y2={245} stroke="#e07b00" strokeWidth="2" markerEnd="url(#stArr)"/>
        <text x={562} y={249} fontSize="11" fill="#e07b00" fontWeight="700">Contact surface</text>
        <text x={562} y={263} fontSize="11" fill="#e07b00">(slower than shock)</text>

        {/* Expansion fan moving left */}
        {[0,1,2,3,4].map(i => (
          <line key={i}
            x1={313} y1={68} x2={313 - 70 - i*15} y2={68 + (1+i)*30}
            stroke="#2E75B6" strokeWidth="1.4" opacity="0.6"/>
        ))}
        {[0,1,2,3,4].map(i => (
          <line key={i}
            x1={313} y1={222} x2={313 - 70 - i*15} y2={222 - (1+i)*30}
            stroke="#2E75B6" strokeWidth="1.4" opacity="0.6"/>
        ))}
        <text x={240} y={58} fontSize="11" fill="#2E75B6" fontWeight="700">Expansion fan (left)</text>

        {/* Region labels after burst */}
        <rect x={320} y={85} width={50} height={54} rx={2} fill="#f5e6ff" stroke="#9b59b6" strokeWidth="1"/>
        <text x={345} y={104} textAnchor="middle" fontSize="11" fill="#5b2c8d" fontWeight="700">Reg.</text>
        <text x={345} y={118} textAnchor="middle" fontSize="12" fill="#5b2c8d" fontWeight="700">3</text>
        <text x={345} y={132} textAnchor="middle" fontSize="10" fill="#5b2c8d">Expanded</text>

        <rect x={476} y={85} width={50} height={54} rx={2} fill="#fff5e6" stroke="#e07b00" strokeWidth="1"/>
        <text x={501} y={104} textAnchor="middle" fontSize="11" fill="#8B4513" fontWeight="700">Reg.</text>
        <text x={501} y={118} textAnchor="middle" fontSize="12" fill="#8B4513" fontWeight="700">2</text>
        <text x={501} y={132} textAnchor="middle" fontSize="10" fill="#8B4513">Shocked</text>

        <rect x={632} y={85} width={50} height={54} rx={2} fill="#e8f4e8" stroke="#27ae60" strokeWidth="1"/>
        <text x={657} y={104} textAnchor="middle" fontSize="11" fill="#145214" fontWeight="700">Reg.</text>
        <text x={657} y={118} textAnchor="middle" fontSize="12" fill="#145214" fontWeight="700">1</text>
        <text x={657} y={132} textAnchor="middle" fontSize="10" fill="#145214">Undisturbed</text>

        {/* Bottom info boxes */}
        <rect x={30} y={252} width={200} height={40} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={36} y={267} fontSize="11" fill="#7B0D0D" fontWeight="700">Shock Mach: Ms = W_s/a₁</text>
        <text x={36} y={283} fontSize="11" fill="#444">W_s can reach Ma = 5–30</text>

        <rect x={240} y={252} width={220} height={40} rx={3} fill="#fff5e6" stroke="#e07b00" strokeWidth="1"/>
        <text x={246} y={267} fontSize="11" fill="#8B4513" fontWeight="700">Test time: μs to ms</text>
        <text x={246} y={283} fontSize="11" fill="#444">Region 2 at high T, p — ideal for</text>

        <rect x={470} y={252} width={390} height={40} rx={3} fill="#e8f4e8" stroke="#27ae60" strokeWidth="1"/>
        <text x={476} y={267} fontSize="11" fill="#145214" fontWeight="700">Applications: combustion, hypersonic testing, plasma, chemical kinetics</text>
        <text x={476} y={283} fontSize="11" fill="#444">Temperature in Region 2 can exceed 10,000 K — useful for dissociation studies</text>
      </svg>
      <div style={S.dCaption}>A shock tube is a fundamental research tool: a high-pressure driver section is separated from a low-pressure test gas by a thin diaphragm. When the diaphragm bursts, a shock wave propagates into the test gas while an expansion fan travels back into the driver. Region 2 (between shock and contact surface) reaches very high temperature and pressure for a brief interval — ideal for studying hypersonic flows, chemical kinetics, and combustion without the expense of a wind tunnel.</div>
    </div>
  );
}

// ── Main ────────────────────────────────────────────────────────
export default function CompressibleFlow() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Compressible Flow in Fluid Dynamics</h1>
      <p style={S.p}>
        In most everyday flows — water pipes, slow fans, gentle breezes — the fluid density stays essentially constant and we treat the flow as incompressible. But when a fluid moves fast enough relative to the local speed of sound, density changes become significant, thermodynamics couples tightly to the flow mechanics, and entirely new phenomena emerge: shock waves, expansion fans, choking, and aerodynamic heating. Compressible flow theory governs everything from jet engines and rocket nozzles to supersonic aircraft, re-entry capsules, and industrial gas pipelines.
      </p>

      <InfoBox title="Core Ideas at a Glance" items={[
        "Mach number Ma = U/a governs compressible flow; compressibility significant for Ma > 0.3",
        "Speed of sound: a = √(γRT) — depends on temperature, not pressure",
        "Isentropic flow: adiabatic + reversible; all properties related to stagnation values through Ma",
        "Normal shock: abrupt irreversible jump — Ma drops below 1, p/T/ρ all rise, total pressure falls",
        "Oblique shock: angled shock on sharp corners — flow deflected, remains supersonic",
        "Prandtl–Meyer expansion fan: isentropic, flow accelerates around convex corners",
        "Choked flow: when Ma = 1 at a throat, mass flow cannot increase by lowering back pressure",
        "de Laval nozzle: converging–diverging geometry converts subsonic to supersonic flow",
        "Aerodynamic heating: stagnation temperature T₀ = T(1 + (γ−1)/2 · Ma²) — critical for re-entry",
      ]}/>

      <h2 style={S.h2}>Mach Number and Flow Regimes</h2>
      <p style={S.p}>
        The Mach number is the ratio of the local flow speed to the local speed of sound. It is the single most important parameter in compressible flow, determining not just the magnitude of compressibility effects but the qualitative nature of the flow — whether pressure disturbances can propagate upstream, whether shock waves form, and how the flow responds to area changes or body geometry. The speed of sound is itself a function of thermodynamic state, varying with temperature as a = √(γRT).
      </p>
      <DiagramMachRegimes/>
      <Table2 rows={[
        ["Mach number","Ma = U/a  (flow speed / local speed of sound)\nDimensionless — most important parameter in compressible flow"],
        ["Speed of sound","a = √(γRT)  for a perfect gas\nγ = ratio of specific heats (1.4 for diatomic air), R = 287 J/kg·K\nAt sea level (288 K): a ≈ 340 m/s"],
        ["Subsonic (Ma < 0.8)","Flow everywhere slower than sound; smooth, no shocks\nPressure disturbances travel ahead — fluid adjusts in advance"],
        ["Transonic (0.8–1.2)","Mixed subsonic/supersonic regions; shocks first appear\nBuffeting, wave drag onset — critical for commercial aircraft design"],
        ["Supersonic (1.2–5)","Flow faster than sound everywhere; shocks, expansion fans\nNo upstream communication of disturbances"],
        ["Hypersonic (Ma > 5)","Extreme aerodynamic heating; dissociation and ionisation of air\nThin shock layer; viscous interaction with boundary layer significant"],
        ["Incompressibility limit","Density change Δρ/ρ ≈ Ma²/2 for isentropic flow\nBelow Ma 0.3: Δρ/ρ < 5% — incompressible assumption valid to ~2%"],
        ["Total (stagnation) temperature","T₀ = T(1 + (γ−1)/2 · Ma²)\nAt Ma 6: T₀/T ≈ 8.2 — re-entry vehicles face enormous heating"],
      ]}/>

      <h2 style={S.h2}>Sound Waves, Mach Cone, and Information Propagation</h2>
      <p style={S.p}>
        A profound consequence of supersonic flight is that pressure disturbances — which travel at the speed of sound — cannot propagate forward into the oncoming flow. The fluid receives no warning that a body is approaching. This fundamental change in the character of the governing equations (from elliptic to hyperbolic) explains why shock waves form and why supersonic aerodynamics is so different from its subsonic counterpart.
      </p>
      <DiagramMachCone/>
      <Table2 rows={[
        ["Mach cone","Envelope of all wave fronts emitted by a supersonic source\nHalf-angle μ = arcsin(1/Ma)  (Mach angle)"],
        ["Zone of silence","Region outside the Mach cone — completely undisturbed\nObserver hears nothing until cone sweeps past"],
        ["Zone of action","Region inside the Mach cone — influenced by the source\nAll flow deflection and shock effects confined here"],
        ["Sonic boom","Pressure jump at the Mach cone surface heard on the ground\nNot instantaneous — continuous overpressure signature (N-wave shape)"],
        ["Doppler effect","Subsonic: frequency ahead higher than behind (f_ahead = f₀/(1−Ma))\nAt Ma = 1: infinite frequency compression ahead — shock forms"],
        ["Hyperbolic equations","Supersonic flow governed by wave (hyperbolic) PDEs — solutions propagate along characteristics\nSubsonic flow governed by elliptic PDEs — influence is everywhere simultaneous"],
        ["Characteristics","Lines in the flow field along which information propagates\nLeft and right running Mach lines: angles ±μ to streamlines"],
      ]}/>

      <h2 style={S.h2}>Isentropic Flow and Stagnation Properties</h2>
      <p style={S.p}>
        When a compressible flow is adiabatic (no heat transfer) and reversible (no shock waves or friction), it is isentropic — entropy is constant. In this case, all thermodynamic properties at any point can be related to the local Mach number and the stagnation (total) conditions, which represent what the fluid properties would be if it were brought to rest reversibly. Stagnation conditions are conserved along streamlines in isentropic flow.
      </p>
      <DiagramIsentropic/>
      <Table2 rows={[
        ["Stagnation temperature","T₀ = T + U²/(2c_p) = T(1 + (γ−1)/2 · Ma²)\nConserved in adiabatic flow even with friction or shocks"],
        ["Stagnation pressure","p₀ = p(1 + (γ−1)/2 · Ma²)^(γ/(γ−1))\nConserved only in isentropic (shock-free) flow"],
        ["Stagnation density","ρ₀ = ρ(1 + (γ−1)/2 · Ma²)^(1/(γ−1))\nWith p₀ and T₀: characterises the reservoir feeding the flow"],
        ["Critical conditions (*)","At Ma = 1: T* = 2T₀/(γ+1), p* = p₀(2/(γ+1))^(γ/(γ−1))\nFor air (γ=1.4): T*/T₀ = 0.833, p*/p₀ = 0.528, ρ*/ρ₀ = 0.634"],
        ["Area–Mach relation","A/A* = (1/Ma)·[(2/(γ+1))·(1+(γ−1)/2·Ma²)]^((γ+1)/(2(γ−1)))\nTwo Ma solutions for each A/A* (one subsonic, one supersonic)"],
        ["Mass flow rate (choked)","ṁ = A*·p₀·√(γ/RT₀)·(2/(γ+1))^((γ+1)/(2(γ−1)))\nDepends only on throat area and stagnation conditions; independent of back pressure"],
        ["Pitot tube (compressible)","Rayleigh Pitot formula for Ma > 1:\np₀₂/p₁ = [(γ+1)²Ma²/(4γMa²−2(γ−1))]^(γ/(γ−1)) · (1−γ+2γMa²)/(γ+1)"],
      ]}/>

      <h2 style={S.h2}>Normal Shock Waves</h2>
      <p style={S.p}>
        A normal shock is the simplest and strongest type of shock wave — a thin discontinuity perpendicular to the flow across which all flow quantities change abruptly. Normal shocks appear inside de Laval nozzles at off-design conditions, ahead of blunt bodies, and in intakes of supersonic engines. They are governed by the Rankine–Hugoniot conditions, derived from conservation of mass, momentum, and energy with no assumptions about the internal structure of the shock.
      </p>
      <DiagramNormalShock/>
      <Table2 rows={[
        ["Conservation laws across shock","Continuity: ρ₁U₁ = ρ₂U₂\nMomentum: p₁ + ρ₁U₁² = p₂ + ρ₂U₂²\nEnergy: h₁ + U₁²/2 = h₂ + U₂²/2  (T₀ conserved)"],
        ["Downstream Mach","Ma₂² = [(γ−1)Ma₁² + 2] / [2γMa₁² − (γ−1)]\nFor Ma₁ = 2: Ma₂ ≈ 0.577; always subsonic after normal shock"],
        ["Pressure ratio","p₂/p₁ = [2γMa₁² − (γ−1)] / (γ+1)\nFor Ma₁ = 2: p₂/p₁ = 4.5 — large pressure jump"],
        ["Temperature ratio","T₂/T₁ = [2γMa₁²−(γ−1)][(γ−1)Ma₁²+2] / [(γ+1)²Ma₁²]\nFor Ma₁ = 2: T₂/T₁ ≈ 1.69 — significant heating"],
        ["Density ratio","ρ₂/ρ₁ = (γ+1)Ma₁² / [(γ−1)Ma₁² + 2]\nMaximum (Ma₁→∞): ρ₂/ρ₁ → (γ+1)/(γ−1) = 6 for air"],
        ["Total pressure ratio","p₀₂/p₀₁ < 1 (always) — measure of shock losses\nFor Ma₁ = 2: p₀₂/p₀₁ ≈ 0.72; for Ma₁ = 5: ≈ 0.06"],
        ["Shock thickness","~10⁻⁷ m (a few molecular mean-free-paths) at sea level\nContinuum mechanics treats it as a mathematical discontinuity"],
        ["Irreversibility","Entropy increases across shock → total pressure decreases\nMore entropy generated by stronger shocks — why multiple weak oblique shocks are preferred in intakes"],
      ]}/>

      <h2 style={S.h2}>Oblique Shocks and Expansion Fans</h2>
      <p style={S.p}>
        Most real supersonic flows involve oblique rather than normal shocks. When supersonic flow encounters a compression corner — a wedge, a ramp, a deflection — an oblique shock forms at a shock angle β relative to the incoming flow, deflecting the flow by the wedge angle θ. The key relationship between Ma, β and θ (the θ–β–Ma relation) determines whether the shock attaches to the tip or detaches as a bow shock. When supersonic flow encounters an expansion corner, no shock forms; instead, a fan of isentropic Mach waves allows the flow to turn smoothly and accelerate.
      </p>
      <DiagramObliqueShock/>
      <Table2 rows={[
        ["Oblique shock angle","θ–β–Ma: tan θ = 2 cot β · (Ma₁²sin²β−1) / (Ma₁²(γ+cos2β)+2)\nTwo solutions (weak and strong shock) for θ < θ_max"],
        ["Weak vs strong shock","Weak: flow remains supersonic (usual); strong: flow becomes subsonic\nStrong solution rarely observed in free flow — requires high back pressure"],
        ["Maximum deflection angle","θ_max increases with Ma; at Ma → ∞: θ_max → 45.5° (γ=1.4)\nAbove θ_max: shock detaches — becomes bow shock"],
        ["Normal shock component","Only component of velocity normal to shock drives shock relations\nMa_n1 = Ma₁ sinβ; apply normal shock with Ma_n1"],
        ["Prandtl–Meyer function","ν(Ma) = √((γ+1)/(γ−1)) · arctan(√((γ−1)(Ma²−1)/(γ+1))) − arctan(√(Ma²−1))\nν₂ = ν₁ + θ_expansion"],
        ["Expansion fan","Isentropic — total pressure conserved; Ma increases, p and T fall\nUsed in rocket nozzle exits, supersonic aircraft nozzles"],
        ["Shock reflection","Oblique shock reflecting off a flat wall: reflected shock brings flow back parallel to wall\nRegular reflection (weak shocks) vs Mach reflection (strong shocks)"],
        ["Shock–expansion method","For supersonic aerofoil: alternate shocks and expansion fans around corners\nSimple hand calculation method giving accurate pressure distribution"],
      ]}/>

      <h2 style={S.h2}>de Laval Nozzle and Choked Flow</h2>
      <p style={S.p}>
        The converging–diverging nozzle, invented by Gustaf de Laval in 1888 for steam turbines, is the key device for generating controlled supersonic flows. It exploits the counterintuitive area–Mach relationship: in subsonic flow, a decreasing area accelerates the gas (venturi effect); in supersonic flow, an increasing area accelerates it further. The throat reaches exactly Ma = 1 and acts as a valve — once choked, no further reduction in back pressure can increase the mass flow rate.
      </p>
      <DiagramNozzle/>
      <Table2 rows={[
        ["Area–velocity relation","dA/A = (Ma²−1) dU/U\nSubsonic: dA < 0 → dU > 0 (converging accelerates)\nSupersonic: dA > 0 → dU > 0 (diverging accelerates)"],
        ["Choking","When throat reaches Ma = 1, mass flow is maximum\nReducing back pressure further cannot increase ṁ — flow is 'choked'"],
        ["Design pressure ratio","p₀/p_exit needed for perfectly expanded supersonic exit flow\nShocks appear inside nozzle if back pressure too high"],
        ["Over-expanded nozzle","p_e < p_ambient: oblique shocks outside nozzle — flow recompressed\nDiamond shock pattern in exhaust plume"],
        ["Under-expanded nozzle","p_e > p_ambient: expansion fans at exit — flow continues to expand\nSeen in rocket exhausts at high altitude"],
        ["Rocket engine nozzle","De Laval nozzle: combustion chamber → converging → throat → diverging\nExpands hot gases from Ma ≈ 0 to Ma 3–5 for thrust generation"],
        ["Wind tunnel","Supersonic wind tunnel uses de Laval nozzle to produce uniform test section flow\nDiffuser section downstream with second throat recovers pressure"],
        ["Critical pressure ratio","p*/p₀ = (2/(γ+1))^(γ/(γ−1))\nFor air: p*/p₀ = 0.528 — back pressure must be below this to choke"],
      ]}/>

      <h2 style={S.h2}>Detached Bow Shocks and Blunt Body Aerodynamics</h2>
      <p style={S.p}>
        Every blunt body moving at supersonic speed — a sphere, a capsule, a flat-nosed aircraft — produces a detached bow shock that curves around the front. Unlike the attached oblique shock on a sharp wedge, the bow shock is strongest (nearly normal) along the centreline stagnation streamline and weaker (more oblique) away from the axis. This geometry creates a subsonic pocket between the shock and the body nose, enabling blunt re-entry capsules to be aerodynamically controlled despite the chaotic shock structure.
      </p>
      <DiagramBowShock/>
      <Table2 rows={[
        ["Bow shock stand-off distance","Distance from shock to body nose; decreases with increasing Ma\nScales with body bluntness — sharper nose → shock closer"],
        ["Entropy layer","Streamlines near stagnation line pass through strongest part of bow shock\n→ high entropy region wraps around body, affecting BL transition"],
        ["Aerodynamic heating","Blunt body: high drag but moderate heating (shock dissipates energy away from surface)\nSharp body: low drag but intense localised heating at the tip"],
        ["Re-entry design","Apollo CM: blunt 33° half-angle; C_D ≈ 1.6, deceleration used to dissipate energy\nSharp nose: lower drag but titanium or carbon-carbon required for thermal loads"],
        ["Wave drag","Additional drag due to shock wave formation — dominant at supersonic speeds\nC_D_wave ∝ (body volume)^(2/3) / (body length)²  (Sears–Haack body minimises this)"],
        ["Sears–Haack body","Theoretical body of minimum wave drag for given volume and length\nArea distribution: S(x) = (16V/3L)(x/L)(1−x/L)^(3/2)"],
        ["Area rule (Whitcomb)","Total cross-sectional area must vary smoothly along aircraft length\nWaisted 'Coke bottle' fuselage reduces wave drag in transonic regime"],
      ]}/>

      <h2 style={S.h2}>Aerodynamic Heating and Hypersonic Flow</h2>
      <p style={S.p}>
        At hypersonic Mach numbers (Ma &gt; 5), the kinetic energy of the flow is so large that stagnation temperatures reach thousands of Kelvin. Air molecules dissociate and ionise, changing γ and making the perfect-gas assumption invalid. The shock layer becomes very thin, viscous effects interact strongly with the shock, and radiation from the hot gas becomes significant. Understanding these phenomena is critical for designing re-entry vehicles, hypersonic cruise missiles, and spaceplanes.
      </p>
      <Table2 rows={[
        ["Stagnation temperature","T₀ = T∞(1 + (γ−1)/2 · Ma²)\nISS re-entry (Ma≈25): T₀ ≈ 74,000 K  (real gas much lower due to dissociation)"],
        ["Aerodynamic heating rate","q̇ ∝ ρ^(1/2) · U³ / R^(1/2)  (Fay–Riddell for stagnation point)\nR = nose radius — larger R reduces heating (why blunt capsules survive)"],
        ["Real gas effects","O₂ dissociates at ~2000 K, N₂ at ~4000 K, ionisation at ~9000 K\nγ decreases from 1.4 → ~1.1–1.2; shock relations change substantially"],
        ["Shock layer","Very thin at high Ma: shock layer thickness ~ body size / Ma²\nViscous–inviscid interaction becomes important"],
        ["Radiation heating","At Ma > 15, radiative heat flux from hot plasma can exceed convective flux\nApollo: radiation ~40% of total heat load at peak heating"],
        ["Ablative heat shields","Surface material ablates (melts, vaporises, chars) carrying heat away\nAblation products cool the boundary layer and block radiation"],
        ["Thermal Protection System","Space Shuttle: re-usable silica tiles (conductivity ≈ 0.07 W/mK)\nOrion: AVCOAT ablative material (~2.4 kg/m² loss per entry)"],
        ["Scramjet","Supersonic Combustion Ramjet: combustion occurs in supersonic airstream\nMa 5–15 operation; fuel must mix and burn in milliseconds"],
      ]}/>

      <h2 style={S.h2}>Shock Tubes and Experimental Methods</h2>
      <p style={S.p}>
        Generating and studying high-speed compressible flows requires specialised facilities. The shock tube is the simplest — and one of the most productive — tools: a single diaphragm burst creates a well-characterised shock wave and allows very short duration access to extreme temperatures and pressures. Supersonic and hypersonic wind tunnels use de Laval nozzles; arc-heated facilities add real-gas effects.
      </p>
      <DiagramShockTube/>
      <Table2 rows={[
        ["Shock tube operation","Diaphragm bursts → shock travels right into test gas, expansion fan left\nTest gas in Region 2 briefly reaches high T and p — enables fast kinetics studies"],
        ["Shock Mach number","Ms = W_s/a₁  (shock speed / ambient sound speed)\nControlled by driver/driven pressure ratio p₄/p₁"],
        ["Shock tunnel","Reflected shock heats gas to reservoir conditions; nozzle expands it to hypersonic\nTest times 1–10 ms but conditions simulate Ma 5–25"],
        ["Expansion tube","No reflected shock; adds expansion section to reach higher specific enthalpy\nBetter simulation of real-gas re-entry conditions"],
        ["Supersonic wind tunnel","De Laval nozzle provides uniform Ma test section; diffuser recovers pressure\nRequires large power (pressure ratio ~100 for Ma 4)"],
        ["Schlieren photography","Images density gradients ∂ρ/∂x via refractive index changes\nShows shock waves, expansion fans, and boundary layers directly"],
        ["Pressure-sensitive paint","Oxygen-quenched luminescence maps surface pressure distribution\nFull-field measurement on complex 3D models"],
        ["CFD for compressible flow","Euler equations (inviscid) capture shocks; Navier-Stokes adds viscosity\nSpecialised shock-capturing schemes (Godunov, Roe, MUSCL) required"],
      ]}/>

      <h2 style={S.h2}>Engineering Applications</h2>
      <Table2 rows={[
        ["Jet engine intake","Must decelerate supersonic flight Ma to subsonic at compressor face\nSeries of oblique shocks more efficient than single normal shock — total pressure recovery"],
        ["Rocket nozzle","De Laval nozzle exhausts combustion gases at Ma 3–5\nVacuum: nozzle expansion optimised for ambient pressure = 0"],
        ["Supersonic aircraft","F-16, Concorde, SR-71: variable geometry intakes, area-ruled fuselage\nWave drag dominates — slender bodies, highly swept delta wings"],
        ["Gas pipelines","Choking can occur at restrictions in high-pressure gas lines\nPressure regulators designed to stay below critical pressure ratio"],
        ["Medical nebulisers","Converging nozzle chokes at throat → stable mass flow independent of downstream conditions\nEnsures consistent drug dose delivery"],
        ["Safety relief valves","Choked flow: mass flow rate set by upstream stagnation conditions\nAllows accurate sizing for pressure vessel protection"],
        ["Transonic buffet","Shock oscillation on wing at transonic speeds causes structural vibration\nLimited by buffet boundary in aircraft flight envelope"],
        ["Steam turbines","de Laval (impulse) turbines use converging–diverging nozzles\nPartial-arc admission; convergent nozzle suffices where pressure ratio < critical"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Mach number:  Ma = U/a,   a = √(γRT)  (γ=1.4, R=287 J/kg·K for air)",
        "Mach angle:  sin μ = 1/Ma   (half-angle of Mach cone)",
        "Isentropic: T₀/T = 1+(γ−1)/2·Ma²;   p₀/p = (T₀/T)^(γ/(γ−1));   ρ₀/ρ = (T₀/T)^(1/(γ−1))",
        "Normal shock — downstream Mach:  Ma₂² = [(γ−1)Ma₁²+2] / [2γMa₁²−(γ−1)]",
        "Normal shock — pressure ratio:  p₂/p₁ = [2γMa₁²−(γ−1)] / (γ+1)",
        "Normal shock — density ratio (max):  ρ₂/ρ₁ → (γ+1)/(γ−1) = 6 as Ma₁ → ∞",
        "Area–Mach:  A/A* = (1/Ma)·[(2/(γ+1))·(1+(γ−1)/2·Ma²)]^((γ+1)/(2(γ−1)))",
        "Critical pressure ratio (choking):  p*/p₀ = (2/(γ+1))^(γ/(γ−1)) = 0.528 for air",
        "Prandtl–Meyer: ν₂ = ν₁ + Δθ   (expansion fan turning angle)",
        "Stagnation temperature at re-entry (Ma=25):  T₀ ≈ T∞ × 126  (~36,000 K ideal gas)",
      ]}/>
    </div>
  );
}